// src/components/ui.jsx
// All shared primitive UI components

import React, { useRef, useEffect } from 'react';

export const Panel = ({ children, title, badge, accent, style, noPad }) => (
  <div style={{
    background: 'var(--surface)',
    border: `1px solid ${accent ? accent + '33' : 'var(--b0)'}`,
    borderRadius: '4px',
    overflow: 'hidden',
    ...style,
  }}>
    {title && (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '9px 16px',
        borderBottom: `1px solid ${accent ? accent + '22' : 'var(--b0)'}`,
        background: 'var(--deep)',
      }}>
        <span style={{ color: accent || 'var(--t3)', fontSize: '10px', letterSpacing: '.14em', textTransform: 'uppercase', fontWeight: 600 }}>
          {title}
        </span>
        {badge && (
          <span style={{
            background: (accent || 'var(--cyan)') + '15',
            color: accent || 'var(--cyan)',
            fontSize: '10px', padding: '2px 9px',
            border: `1px solid ${(accent || 'var(--cyan)') + '33'}`,
            letterSpacing: '.07em', borderRadius: '2px',
          }}>{badge}</span>
        )}
      </div>
    )}
    <div style={noPad ? {} : { padding: '16px' }}>{children}</div>
  </div>
);

export const Slider = ({ label, value, min, max, step = 0.01, onChange, unit = '', color = 'var(--cyan)' }) => (
  <div style={{ marginBottom: '14px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
      <span style={{ color: 'var(--t2)', fontSize: '11px' }}>{label}</span>
      <span style={{ color, fontSize: '11px', fontWeight: 600 }}>
        {typeof value === 'number' ? value.toFixed(step < 0.01 ? 4 : step < 1 ? 2 : 0) : value}{unit}
      </span>
    </div>
    <div style={{ position: 'relative', height: '3px', background: 'var(--b0)', borderRadius: '2px' }}>
      <div style={{
        position: 'absolute', left: 0, height: '100%', borderRadius: '2px',
        background: `linear-gradient(90deg,${color}44,${color})`,
        width: `${((value - min) / (max - min)) * 100}%`,
        transition: 'width .08s', pointerEvents: 'none',
      }} />
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        style={{ position: 'absolute', inset: '-7px 0', width: '100%', opacity: 0, cursor: 'pointer', height: '18px' }}
      />
    </div>
  </div>
);

export const Metric = ({ label, value, unit = '', color = 'var(--cyan)', sub }) => (
  <div style={{
    background: 'var(--raised)', border: '1px solid var(--b0)',
    padding: '13px 15px', borderRadius: '3px', borderLeft: `2px solid ${color}`,
  }}>
    <div style={{ color: 'var(--t3)', fontSize: '9px', letterSpacing: '.13em', textTransform: 'uppercase', marginBottom: '5px' }}>{label}</div>
    <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '20px', color }}>
      {value}
      <span style={{ fontSize: '11px', color: 'var(--t2)', marginLeft: '3px', fontFamily: 'JetBrains Mono', fontWeight: 400 }}>{unit}</span>
    </div>
    {sub && <div style={{ fontSize: '10px', color: 'var(--t3)', marginTop: '3px' }}>{sub}</div>}
  </div>
);

export const RunBtn = ({ running, onRun, onStop, label = 'EXECUTE' }) => (
  <button
    onClick={running ? onStop : onRun}
    style={{
      background: running ? 'var(--gred)' : 'var(--gcyan)',
      border: `1px solid ${running ? 'var(--red)' : 'var(--cyan)'}`,
      color: running ? 'var(--red)' : 'var(--cyan)',
      padding: '9px 22px', borderRadius: '3px', cursor: 'pointer',
      fontFamily: 'JetBrains Mono', fontSize: '11px', letterSpacing: '.12em',
      textTransform: 'uppercase', fontWeight: 600,
      display: 'flex', alignItems: 'center', gap: '8px', transition: 'all .15s',
    }}
  >
    {running
      ? <><span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--red)', animation: 'pulse 1s infinite', flexShrink: 0 }} />HALT</>
      : <><span style={{ width: 0, height: 0, borderLeft: '7px solid var(--cyan)', borderTop: '4px solid transparent', borderBottom: '4px solid transparent' }} />{label}</>
    }
  </button>
);

export const DepthToggle = ({ mode, setMode }) => (
  <div style={{ display: 'flex', gap: '2px', background: 'var(--deep)', border: '1px solid var(--b0)', borderRadius: '3px', padding: '2px' }}>
    {['BEGINNER', 'ENGINEER', 'RESEARCH'].map(m => (
      <button key={m} onClick={() => setMode(m)}
        style={{
          padding: '5px 13px', border: 'none', cursor: 'pointer', fontSize: '9px',
          letterSpacing: '.1em', fontFamily: 'JetBrains Mono',
          background: mode === m ? 'var(--cyan)' : 'transparent',
          color: mode === m ? 'var(--void)' : 'var(--t3)',
          fontWeight: mode === m ? 700 : 400, borderRadius: '2px', transition: 'all .12s',
        }}>
        {m}
      </button>
    ))}
  </div>
);

export const LogPanel = ({ logs, height = 110 }) => {
  const ref = useRef();
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [logs]);
  return (
    <div ref={ref} style={{
      background: 'var(--deep)', border: '1px solid var(--b0)',
      height, overflow: 'auto', padding: '10px 14px', fontSize: '11px',
      borderRadius: '3px', lineHeight: 1.6,
    }}>
      {logs.map((l, i) => (
        <div key={i} style={{
          marginBottom: '2px',
          color: l.t === 'err' ? 'var(--red)' : l.t === 'ok' ? 'var(--green)' : l.t === 'warn' ? 'var(--gold)' : 'var(--t2)',
        }}>
          <span style={{ color: 'var(--t3)' }}>[{l.ts}]</span> {l.msg}
        </div>
      ))}
      <span style={{ animation: 'blink 1s infinite', color: 'var(--cyan)' }}>█</span>
    </div>
  );
};

export const Tag = ({ children, color = 'var(--cyan)' }) => (
  <span style={{
    padding: '2px 8px',
    background: color + '15',
    border: `1px solid ${color}44`,
    fontSize: '10px', color, borderRadius: '2px', letterSpacing: '.06em',
  }}>{children}</span>
);

export const Spinner = ({ size = 14 }) => (
  <div style={{
    width: size, height: size,
    border: '2px solid var(--b1)', borderTopColor: 'var(--cyan)',
    borderRadius: '50%', animation: 'spin .8s linear infinite', flexShrink: 0,
  }} />
);

export const Select = ({ label, value, onChange, options }) => (
  <div style={{ marginBottom: '10px' }}>
    {label && <label style={{ fontSize: '9px', color: 'var(--t3)', letterSpacing: '.1em', display: 'block', marginBottom: '4px' }}>{label}</label>}
    <select
      value={value} onChange={e => onChange(e.target.value)}
      style={{
        width: '100%', background: 'var(--deep)', border: '1px solid var(--b1)',
        color: 'var(--t1)', padding: '7px 9px', fontSize: '11px',
        fontFamily: 'JetBrains Mono', borderRadius: '2px', cursor: 'pointer',
      }}
    >
      {options.map(o => (
        <option key={o.value || o} value={o.value || o}>{o.label || o}</option>
      ))}
    </select>
  </div>
);

export const Tabs = ({ tabs, active, onChange, dataset }) => (
  <div style={{
    display: 'flex', gap: '2px', background: 'var(--deep)',
    border: '1px solid var(--b0)', borderRadius: '4px', padding: '3px',
    overflowX: 'auto', flexShrink: 0,
  }}>
    {tabs.map(t => (
      <button
        key={t.id}
        onClick={() => (!t.requiresData || dataset) && onChange(t.id)}
        disabled={t.requiresData && !dataset}
        style={{
          padding: '8px 15px', border: 'none',
          cursor: (t.requiresData && !dataset) ? 'not-allowed' : 'pointer',
          fontSize: '10px', letterSpacing: '.1em', fontFamily: 'JetBrains Mono',
          background: active === t.id ? 'var(--cyan)' : 'transparent',
          color: active === t.id ? 'var(--void)' : (t.requiresData && !dataset) ? 'var(--t3)' : 'var(--t2)',
          fontWeight: active === t.id ? 700 : 400, borderRadius: '3px',
          transition: 'all .12s', whiteSpace: 'nowrap',
          display: 'flex', alignItems: 'center', gap: '5px',
          opacity: (t.requiresData && !dataset) ? 0.4 : 1,
        }}
      >
        {t.icon && <span>{t.icon}</span>}{t.label}
      </button>
    ))}
  </div>
);

export const InfoBox = ({ children, color = 'var(--cyan)', icon = '◈' }) => (
  <div style={{
    padding: '10px 13px', background: color + '10',
    border: `1px solid ${color}33`, borderRadius: '3px',
    fontSize: '11px', color: 'var(--t2)', lineHeight: 1.6,
    display: 'flex', gap: '10px', alignItems: 'flex-start',
  }}>
    <span style={{ color, flexShrink: 0 }}>{icon}</span>
    <span>{children}</span>
  </div>
);

export const timestamp = () =>
  new Date().toLocaleTimeString('en', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
